import FadeIn from './FadeIn';

const gold = '#C8A96E';
const charcoal = '#1A1916';
const secondary = '#6B6862';

const SectionRule = () => (
  <div style={{ width: '32px', height: '1px', backgroundColor: gold, margin: '0 auto 72px' }} />
);

const Label = ({ children }) => (
  <p style={{ color: gold, fontSize: '0.7rem', fontWeight: 500, letterSpacing: '0.22em', textTransform: 'uppercase', marginBottom: '16px' }}>{children}</p>
);

const AboutPage = ({ language = 'en' }) => {
  return (
    <div className="min-h-screen bg-white">
      <div style={{ paddingTop: 'env(safe-area-inset-top)', position: 'sticky', top: 0, zIndex: 50, backgroundColor: '#fff' }} />

      {/* ─── HERO ─────────────────────────────────────────────── */}
      <section className="px-6 text-center" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        <Label>{language === 'es' ? 'Acerca de' : 'About'}</Label>
        <h1 className="font-light" style={{ color: charcoal, fontSize: 'clamp(3rem, 8vw, 5.5rem)', lineHeight: '1.1', letterSpacing: '-0.02em', marginBottom: '20px' }}>
          Zeus Hernandez
        </h1>
        <p className="font-light text-lg" style={{ color: secondary, lineHeight: '1.7', maxWidth: '480px', margin: '0 auto 28px' }}>
          {language === 'es'
            ? 'Desarrollador inmobiliario, constructor de plataformas y pensador de largo horizonte.'
            : 'Real estate developer, platform builder, and long-horizon thinker.'}
        </p>
        <div style={{ width: '32px', height: '1px', backgroundColor: gold, margin: '0 auto' }} />
        <div className="scroll-arrow" style={{ position: 'absolute', bottom: '40px', left: '50%', transform: 'translateX(-50%)' }}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 3v14M3 10l7 7 7-7" stroke={gold} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </section>

      {/* ─── SECTION 1 — IDENTITY ─────────────────────────────── */}
      <section className="pt-12 pb-20 px-6">
        <FadeIn>
          <div className="max-w-5xl mx-auto grid gap-10 lg:grid-cols-[0.86fr_1fr] lg:items-start">
            <div className="relative overflow-hidden bg-black h-[420px] max-w-sm mx-auto sm:h-[520px] lg:max-w-none lg:h-[620px]" style={{ borderRadius: '18px' }}>
              <img
                src="/image2.webp"
                alt={language === 'es' ? 'Retrato profesional de Zeus Hernandez' : 'Professional portrait of Zeus Hernandez'}
                className="h-full w-full object-cover"
                style={{ objectPosition: '50% 24%', filter: 'contrast(1.04)' }}
              />
            </div>
            <div style={{ maxWidth: '560px', margin: '0 auto' }}>
              <Label>{language === 'es' ? 'Identidad' : 'Identity'}</Label>
              <h2 className="font-light" style={{ color: charcoal, fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', lineHeight: '1.2', marginBottom: '32px' }}>
                {language === 'es' ? 'Acerca de' : 'About'}
              </h2>
              {[
                'Zeus Hernandez is a real estate developer and platform builder operating at the intersection of real assets, institutional governance, and technical innovation.',
                'He is the Founder & CEO of Infinity Development, a commercial real estate investment and development firm focused on sustainable multifamily and living-sector projects across the United States and Latin America. Educated in real estate at UCLA, his work spans the full development lifecycle — from site acquisition and capital structuring to construction and long-term asset strategy.',
                'He is also the host of The Infinity Podcast, a bilingual platform featuring conversations with investors, developers, and operators shaping the future of the built environment, capital strategy, and long-term value creation.',
              ].map((para, i) => (
                <p key={i} className="font-light text-sm" style={{ color: secondary, lineHeight: '2', marginBottom: '20px' }}>{para}</p>
              ))}
            </div>
          </div>
        </FadeIn>
      </section>

      <SectionRule />

      {/* ─── SECTION 2 — BELIEF ───────────────────────────────── */}
      <section className="py-20 px-6">
        <FadeIn>
          <div style={{ maxWidth: '560px', margin: '0 auto' }}>
            <Label>{language === 'es' ? 'Creencia' : 'Belief'}</Label>
            <h2 className="font-light" style={{ color: charcoal, fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', lineHeight: '1.2', marginBottom: '32px' }}>
              {language === 'es' ? 'Por Qué Construyo' : 'Why I Build'}
            </h2>
            {[
              "I've always been drawn to building — not just structures, but systems, ideas, and platforms that endure.",
              'To me, real estate is one of the most consequential industries in the world. It shapes economies, communities, and the daily lives of millions. It sits at the intersection of design, capital, technology, and human behavior.',
              'Today, with the rise of AI, sustainability, and global connectivity, we are entering a moment where the built environment can be rethought entirely — not just optimized, but fundamentally redesigned.',
              'My work is rooted in that belief: that what we build should not only perform, but endure.',
            ].map((para, i) => (
              <p key={i} className="font-light text-sm" style={{ color: secondary, lineHeight: '2', marginBottom: '20px' }}>{para}</p>
            ))}
          </div>
        </FadeIn>
      </section>

      <SectionRule />

      {/* ─── SECTION 3 — DEFINING MOMENT ─────────────────────── */}
      <section className="py-20 px-6">
        <FadeIn>
          <div style={{ maxWidth: '560px', margin: '0 auto' }}>
            <Label>{language === 'es' ? 'Un Momento Definitorio' : 'A Defining Moment'}</Label>
            <h2 className="font-light" style={{ color: charcoal, fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', lineHeight: '1.2', marginBottom: '32px' }}>
              {language === 'es' ? 'Un Momento Definitorio' : 'A Defining Moment'}
            </h2>
            <div style={{ borderLeft: `2px solid ${gold}`, paddingLeft: '24px', marginBottom: '32px' }}>
              {[
                'Early in my journey, a single moment reshaped how I think about time, urgency, and responsibility.',
                'After surviving a severe car crash outside Las Vegas, I was forced to confront a simple truth: time is not promised.',
                "What stayed with me wasn't fear — it was clarity. Comfort disappeared. Hesitation no longer made sense.",
                "That experience didn't change what I wanted to build. It changed how seriously I took the responsibility to build it well.",
                'From that point forward, I committed fully to creating work that would outlast me — work rooted in conviction, discipline, and long-term impact.',
              ].map((para, i) => (
                <p key={i} className="font-light text-sm" style={{ color: secondary, lineHeight: '2', marginBottom: '20px' }}>{para}</p>
              ))}
            </div>
            <p className="font-normal text-sm" style={{ color: charcoal, lineHeight: '2' }}>
              {language === 'es' ? 'Infinity nació de esa comprensión.' : 'Infinity was born from that realization.'}
            </p>
          </div>
        </FadeIn>
      </section>

      <SectionRule />

      {/* ─── SECTION 4 — PLATFORM ─────────────────────────────── */}
      <section className="py-20 px-6">
        <FadeIn>
          <div style={{ maxWidth: '560px', margin: '0 auto' }}>
            <Label>{language === 'es' ? 'La Plataforma' : 'The Platform'}</Label>
            <h2 className="font-light" style={{ color: charcoal, fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', lineHeight: '1.2', marginBottom: '32px' }}>
              {language === 'es' ? 'Construyendo Hacia el Infinito' : 'Building Toward Infinity'}
            </h2>
            {[
              'Infinity Development is the foundation of my work — but not the endpoint.',
              'I am building toward a broader, long-term platform: Infinity. A multi-decade vision designed to operate across real assets, innovation, and impact-driven initiatives.',
              'Over time, this platform will expand into verticals such as venture investing, energy, ocean systems, space and beyond, and philanthropic initiatives — each developed deliberately, with independent leadership, governance, and accountability.',
              'This is not about scale for its own sake. It is about stewardship — deploying capital, technology, and design in ways that create durable value across markets and generations.',
              'Infinity is being built patiently. Methodically. With institutional discipline. One vertical at a time.',
            ].map((para, i) => (
              <p key={i} className="font-light text-sm" style={{ color: secondary, lineHeight: '2', marginBottom: '20px' }}>{para}</p>
            ))}
          </div>
        </FadeIn>
      </section>

      <SectionRule />

      {/* ─── SECTION 5 — DIRECTION ────────────────────────────── */}
      <section className="py-20 px-6">
        <FadeIn>
          <div style={{ maxWidth: '560px', margin: '0 auto' }}>
            <Label>{language === 'es' ? 'Dirección' : 'Direction'}</Label>
            <h2 className="font-light" style={{ color: charcoal, fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', lineHeight: '1.2', marginBottom: '32px' }}>
              {language === 'es' ? 'Dirección' : 'Direction'}
            </h2>
            {[
              'My work is centered on building systems that create lasting value: Economically, Socially, and Environmentally.',
              'Through real estate development, research, and storytelling, I aim to contribute to a shift in how we think about building: moving from short-term outcomes to long-term impact.',
              'I believe we have a responsibility not just to consume and extract, but to design, restore, and contribute.',
            ].map((para, i) => (
              <p key={i} className="font-light text-sm" style={{ color: secondary, lineHeight: '2', marginBottom: '20px' }}>{para}</p>
            ))}
            <div style={{ borderLeft: `2px solid ${gold}`, paddingLeft: '24px', marginTop: '32px' }}>
              {[
                'A future where cities regenerate the planet.',
                'Where capital aligns with long-term human outcomes.',
                'Where technology amplifies — rather than replaces — our ability to build meaningfully.',
                'And where our future is intelligent, sustainable, and principled by design.',
              ].map((line, i) => (
                <p key={i} className="font-light text-sm" style={{ color: secondary, lineHeight: '2.2', marginBottom: '4px' }}>
                  — {line}
                </p>
              ))}
            </div>
          </div>
        </FadeIn>
      </section>

      <SectionRule />

      {/* ─── SECTION 6 — CLOSING ──────────────────────────────── */}
      <section className="py-20 px-6 text-center">
        <FadeIn>
          <div style={{ maxWidth: '448px', margin: '0 auto' }}>
            <Label>{language === 'es' ? 'Cierre' : 'Closing'}</Label>
            {[
              language === 'es' ? 'Estoy al inicio del camino, pero claro en la dirección.' : 'I am early in the journey, but clear on the direction.',
              language === 'es' ? 'Estoy construyendo con deliberación.' : 'I am building deliberately.',
              language === 'es' ? 'Estoy pensando a largo plazo.' : 'I am thinking long-term.',
              language === 'es'
                ? 'Y busco alinearme con quienes comparten esa mentalidad — para construir juntos.'
                : 'And I am looking to align with others who share that mindset — to build alongside.',
            ].map((line, i) => (
              <p key={i} className="font-light" style={{ color: charcoal, fontSize: '1rem', lineHeight: '2', marginBottom: '4px' }}>
                {line}
              </p>
            ))}
            <div style={{ width: '32px', height: '1px', backgroundColor: gold, margin: '40px auto 0' }} />
          </div>
        </FadeIn>
      </section>
    </div>
  );
};

export default AboutPage;
